import { WaypointActionType, WaypointActionStatusName, FeatureFlag, SupplyItemStatus } from '@caresend/types';
import { ButtonStyle } from '../../../../../../buttons/ButtonComponent/model.js';
import { extractTarget } from '../../helpers/helpers.js';
import { getCommittedWorkflow, existingWorkflowMap, getWorkflowItemInitializer, maybeAddToMap, sortWorkflowByPriority } from '../helpers.js';
import { WaypointActionWorkflowContextID, WaypointActionWorkflowItemID, ProcedureWorkflowContextID } from './model.js';
import { WorkflowModalComponentName, WorkflowInlineComponentName } from '../model.js';
import { getStore } from '../../../../../../../store/helpers/initStore.js';

/**
 * Patient action waypoint workflow items
 * - Procedures
 * - Patient DOB
 * - Unlock supplies
 * - Patient consent
 */
const getPatientActionWaypointWorkflowItems = (waypointAction, contextID = WaypointActionWorkflowContextID.DEFAULT, parentReferenceID, { waypointActions, procedures }, forcePatientConsent = false) => {
    var _a;
    if (!waypointActions)
        return [];
    const priorities = {};
    const workflow = getCommittedWorkflow(waypointAction, parentReferenceID, contextID);
    const workflowMap = existingWorkflowMap(workflow);
    let priority = 1;
    const initWorkflowItem = getWorkflowItemInitializer(waypointAction.id, 'waypointActions', {
        storeRef: 'waypoints',
        context: contextID,
        referenceID: parentReferenceID,
    });
    /** Packing workflow item */
    const someProcedureRequiresOfflineSupplies = waypointActions.getters
        .someProcedureRequiresOfflineSupplies(waypointAction.id);
    if (someProcedureRequiresOfflineSupplies) {
        const packing = initWorkflowItem(WaypointActionWorkflowItemID.PROCEDURES, {
            afterFailure: {
                status: {
                    status: WaypointActionStatusName.CANCELED,
                },
            },
            disabled: false,
        }, {
            context: ProcedureWorkflowContextID.OFFLINE_SUPPLY_PACKING,
            key: WaypointActionWorkflowItemID.PROCEDURES,
        });
        priorities[packing.id] = priority;
        priority += 1;
        maybeAddToMap(packing, workflowMap);
    }
    /** Patient DOB workflow item */
    const patientDOB = initWorkflowItem(WaypointActionWorkflowItemID.PATIENT_DOB, {
        modal: {
            name: WorkflowModalComponentName.PATIENT_DOB,
            title: 'Validate identity',
            buttonProps: {
                label: 'Validate identity',
                iconRight: 'unlock',
            },
            manualTrigger: true,
            props: {
                waypointActionID: waypointAction.id,
            },
        },
    });
    priorities[patientDOB.id] = priority;
    priority += 1;
    maybeAddToMap(patientDOB, workflowMap);
    /** Unlock supplies workflow item */
    const allKitSupplyItemsUndefined = Object.keys((_a = waypointAction.procedures) !== null && _a !== void 0 ? _a : {})
        .every((procedureID) => { var _a; return ((_a = procedures === null || procedures === void 0 ? void 0 : procedures.getRaw(procedureID)) === null || _a === void 0 ? void 0 : _a.kitSupplyItem) === undefined; });
    if (!allKitSupplyItemsUndefined) {
        const unlockSupplies = initWorkflowItem(WaypointActionWorkflowItemID.UNLOCK_SUPPLIES, {
            modal: {
                name: WorkflowModalComponentName.SCANNER,
                title: 'Unlock supplies',
                buttonProps: {
                    label: 'Unlock supplies',
                    iconRight: 'chevron-right',
                },
                props: {
                    recordManagerID: 'waypointActions',
                    recordManagerTargetID: waypointAction.id,
                    forceClickMode: getStore().getters['variables/getFeatureFlagByName'](FeatureFlag.DISABLE_PATIENT_VISIT_SUPPLY_SCANNING),
                    offlineMode: true,
                    expectedSupplyItemStatus: SupplyItemStatus.ACTIVATED,
                    initFooterButtonDisabled: true,
                },
            },
        });
        priorities[unlockSupplies.id] = priority;
        priority += 1;
        maybeAddToMap(unlockSupplies, workflowMap);
    }
    const hasKitSupplyStrategy = waypointActions.getters.isKitShipmentSupplyStrategy(waypointAction.id);
    if (!hasKitSupplyStrategy || forcePatientConsent) {
        /** Patient consent workflow item */
        const patientConsent = initWorkflowItem(WaypointActionWorkflowItemID.PATIENT_CONSENT, {
            modal: {
                name: WorkflowModalComponentName.PATIENT_CONSENT,
                title: 'Patient consent',
                buttonProps: {
                    label: 'Patient consent',
                    iconRight: 'chevron-right',
                },
                props: {
                    waypointActionID: waypointAction.id,
                },
            },
        });
        priorities[patientConsent.id] = priority;
        priority += 1;
        maybeAddToMap(patientConsent, workflowMap);
    }
    if (hasKitSupplyStrategy) {
        const followKitInstructions = initWorkflowItem(WaypointActionWorkflowItemID.FOLLOW_KIT_INSTRUCTIONS, {
            modal: {
                name: WorkflowModalComponentName.FOLLOW_KIT_INSTRUCTIONS,
                props: {
                    waypointActionID: waypointAction.id,
                },
            },
        });
        priorities[followKitInstructions.id] = priority;
        priority += 1;
        maybeAddToMap(followKitInstructions, workflowMap);
    }
    /** Procedure default workflow item */
    const procedureDefault = initWorkflowItem(WaypointActionWorkflowItemID.PROCEDURES, {
        after: {
            status: {
                status: WaypointActionStatusName.COMPLETE,
            },
        },
        afterFailure: {
            status: {
                status: WaypointActionStatusName.CANCELED,
            },
        },
        component: {
            name: WorkflowInlineComponentName.TODO,
            props: {
                text: 'Procedure default',
            },
        },
    }, {
        key: WaypointActionWorkflowItemID.PROCEDURES,
        context: ProcedureWorkflowContextID.DEFAULT,
        /** TODO: Reinstate if needed */
        // failureContext: ProcedureWorkflowContextID.DEFAULT_FAILURE,
    });
    priorities[procedureDefault.id] = priority;
    priority += 1;
    maybeAddToMap(procedureDefault, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
/**
 * Drop-off samples waypoint workflow items
 * - Procedures
 * - Scan specimen bag
 * - Present requisition form
 */
const getDropOffWorkflowItems = (waypointAction, contextID = WaypointActionWorkflowContextID.DEFAULT, parentReferenceID, { waypointActions }) => {
    if (!waypointActions || !waypointAction)
        return [];
    const workflow = getCommittedWorkflow(waypointAction, parentReferenceID, contextID);
    const workflowMap = existingWorkflowMap(workflow);
    const priorities = {};
    const initWorkflowItem = getWorkflowItemInitializer(waypointAction.id, 'waypointActions', {
        context: contextID,
        referenceID: parentReferenceID,
        storeRef: 'waypoints',
    });
    /** idk if this is needed, i just put this here */
    const procedures = initWorkflowItem(WaypointActionWorkflowItemID.PROCEDURES, {
        after: {
            status: {
                status: WaypointActionStatusName.COMPLETE,
            },
        },
        afterFailure: {
            status: {
                status: WaypointActionStatusName.CANCELED,
            },
        },
        component: {
            name: WorkflowInlineComponentName.TODO,
            props: {
                text: 'Procedures',
            },
        },
    }, {
        key: WaypointActionWorkflowItemID.PROCEDURES,
        context: ProcedureWorkflowContextID.DROP_OFF_SAMPLES,
    });
    priorities[procedures.id] = 1;
    maybeAddToMap(procedures, workflowMap);
    /** TODO: Add workflow items */
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
/**
 * Mail-in packing waypoint workflow items
 * - Mail-in
 */
const getMailInDefaultWorkflowItems = (waypointAction, contextID = WaypointActionWorkflowContextID.DEFAULT, parentReferenceID, { waypointActions }) => {
    if (!waypointActions || !waypointAction)
        return [];
    const workflow = getCommittedWorkflow(waypointAction, parentReferenceID, contextID);
    const workflowMap = existingWorkflowMap(workflow);
    const priorities = {};
    const initWorkflowItem = getWorkflowItemInitializer(waypointAction.id, 'waypointActions', {
        context: contextID,
        referenceID: parentReferenceID,
        storeRef: 'waypoints',
    });
    const procedures = initWorkflowItem(WaypointActionWorkflowItemID.PROCEDURES, {
        after: {
            status: {
                status: WaypointActionStatusName.COMPLETE,
            },
        },
        afterFailure: {
            status: {
                status: WaypointActionStatusName.CANCELED,
            },
        },
        component: {
            name: WorkflowInlineComponentName.TODO,
            props: {
                text: 'Mail-in',
            },
        },
    }, {
        key: WaypointActionWorkflowItemID.PROCEDURES,
        context: ProcedureWorkflowContextID.MAIL_IN_PACKING,
    });
    priorities[procedures.id] = 1;
    maybeAddToMap(procedures, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
/**
 * Mail-in packing waypoint workflow items
 * - Scan mail-in box
 * - Packing instructions
 * - Pack specimen bags
 */
const getMailInPackingWorkflowItems = (waypointAction, contextID = WaypointActionWorkflowContextID.MAIL_IN_PACKING, parentReferenceID, { waypointActions }) => {
    if (!waypointActions || !waypointAction)
        return [];
    const workflow = getCommittedWorkflow(waypointAction, parentReferenceID, contextID);
    const workflowMap = existingWorkflowMap(workflow);
    const priorities = {};
    const initWorkflowItem = getWorkflowItemInitializer(waypointAction.id, 'waypointActions', {
        context: contextID,
        referenceID: parentReferenceID,
        storeRef: 'waypoints',
    });
    const { mailInBoxSupplyID } = waypointAction;
    const scanMailInBox = initWorkflowItem(WaypointActionWorkflowItemID.SCAN_MAIL_IN_BOX, {
        modal: {
            name: WorkflowModalComponentName.SCANNER,
            title: 'Scan shipping supply',
            buttonProps: {
                label: 'Start packing',
                iconRight: 'box',
                buttonStyle: ButtonStyle.OUTLINE,
            },
            props: {
                recordManagerID: 'waypointActions',
                recordManagerTargetID: waypointAction.id,
                expectedSupplyID: mailInBoxSupplyID,
                expectedSupplyItemStatus: SupplyItemStatus.ACTIVATED,
                initFooterButtonDisabled: true,
                actionOnScan: 'setMailInBoxOnWaypointAction',
            },
        },
    });
    priorities[scanMailInBox.id] = 1;
    maybeAddToMap(scanMailInBox, workflowMap);
    const packingInstructions = initWorkflowItem(WaypointActionWorkflowItemID.PACKING_INSTRUCTIONS, {
        modal: {
            name: WorkflowModalComponentName.EMBED,
            title: 'Packing instructions',
            buttonProps: {
                label: 'Continue packing',
                iconRight: 'box',
            },
            props: {
                embedPath: 'nurseApp.packing',
            },
        },
    });
    priorities[packingInstructions.id] = 2;
    maybeAddToMap(packingInstructions, workflowMap);
    const packSpecimenBags = initWorkflowItem(WaypointActionWorkflowItemID.PACK_SPECIMEN_BAGS, {
        modal: {
            name: WorkflowModalComponentName.SCANNER,
            title: 'Pack specimen bags',
            buttonProps: {
                label: 'Pack specimen bags',
                iconRight: 'box',
            },
            props: {
                recordManagerID: 'waypointActions',
                recordManagerTargetID: waypointAction.id,
                expectedSupplyItemStatus: SupplyItemStatus.ACTIVATED,
                initFooterButtonDisabled: true,
            },
        },
    });
    priorities[packSpecimenBags.id] = 3;
    maybeAddToMap(packSpecimenBags, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
/** contextualized mail-in packing waypoint workflow items */
const getMailInWorkflowItems = (waypointAction, contextID = WaypointActionWorkflowContextID.DEFAULT, parentReferenceID, { waypointActions }) => {
    if (!waypointActions || !waypointAction)
        return [];
    switch (contextID) {
        case WaypointActionWorkflowContextID.MAIL_IN_PACKING:
            return getMailInPackingWorkflowItems(waypointAction, contextID, parentReferenceID, { waypointActions });
        default:
            return getMailInDefaultWorkflowItems(waypointAction, contextID, parentReferenceID, { waypointActions });
    }
};
/** Main contextualized waypoint action workflow items */
const getWaypointActionWorkflow = (wpa, contextID = WaypointActionWorkflowContextID.DEFAULT, parentReferenceID, { waypointActions }, forcePatientConsent = false) => {
    /** Extract waypoint action */
    if (!waypointActions)
        return [];
    const waypointAction = extractTarget(wpa, waypointActions.getRawClone);
    if (!waypointAction)
        return [];
    const isMailInAction = (input) => input.type === WaypointActionType.MAIL_IN;
    switch (waypointAction.type) {
        case WaypointActionType.PATIENT_ACTION:
            return getPatientActionWaypointWorkflowItems(waypointAction, contextID, parentReferenceID, { waypointActions }, forcePatientConsent);
        case WaypointActionType.DROPOFF:
            return getDropOffWorkflowItems(waypointAction, contextID, parentReferenceID, { waypointActions });
        case WaypointActionType.MAIL_IN:
            if (!isMailInAction(waypointAction))
                return [];
            return getMailInWorkflowItems(waypointAction, contextID, parentReferenceID, { waypointActions });
        default:
            return [];
    }
};

export { getWaypointActionWorkflow };
